import { 
  collection, 
  addDoc, 
  query, 
  orderBy, 
  limit, 
  onSnapshot,
  serverTimestamp,
  Timestamp,
  where,
  getDocs
} from 'firebase/firestore';
import { db } from './firebase';

export interface ChatMessage {
  id: string;
  tableId: string;
  senderId: string;
  senderName: string;
  message: string;
  timestamp: Timestamp | null;
  type: 'user' | 'system'; // system messages for joins, leaves, wins, etc.
}

// Send a chat message to a specific table
export const sendMessage = async (
  tableId: string,
  senderId: string,
  senderName: string,
  message: string,
  type: 'user' | 'system' = 'user'
): Promise<string> => {
  try {
    const messagesRef = collection(db, 'tables', tableId, 'messages');
    
    const docRef = await addDoc(messagesRef, {
      tableId,
      senderId,
      senderName,
      message: message.trim(),
      timestamp: serverTimestamp(),
      type,
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

// Send a system message (for events like joins, wins, etc.)
export const sendSystemMessage = async (
  tableId: string,
  message: string
): Promise<string> => {
  return sendMessage(tableId, 'system', 'System', message, 'system');
};

// Subscribe to messages for a table (real-time)
export const subscribeToMessages = (
  tableId: string,
  callback: (messages: ChatMessage[]) => void,
  messageLimit: number = 50
): (() => void) => {
  const messagesRef = collection(db, 'tables', tableId, 'messages');
  const q = query(
    messagesRef,
    orderBy('timestamp', 'desc'),
    limit(messageLimit)
  );
  
  const unsubscribe = onSnapshot(q, (snapshot) => {
    const messages: ChatMessage[] = [];
    snapshot.forEach((doc) => {
      messages.push({
        id: doc.id,
        ...doc.data(),
      } as ChatMessage);
    });
    // Reverse to show oldest first
    callback(messages.reverse());
  }, (error) => {
    console.error('Error subscribing to messages:', error);
  });
  
  return unsubscribe;
};

// Get recent messages (non-realtime)
export const getRecentMessages = async (
  tableId: string,
  messageLimit: number = 50
): Promise<ChatMessage[]> => {
  try {
    const messagesRef = collection(db, 'tables', tableId, 'messages');
    const q = query(
      messagesRef,
      orderBy('timestamp', 'desc'),
      limit(messageLimit)
    );
    
    const snapshot = await getDocs(q);
    const messages: ChatMessage[] = [];
    snapshot.forEach((doc) => {
      messages.push({
        id: doc.id,
        ...doc.data(),
      } as ChatMessage);
    });
    
    return messages.reverse();
  } catch (error) {
    console.error('Error getting messages:', error);
    return [];
  }
};